import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  FileUpload,
  Icon,
  Text,
} from '@civilgrid/ui/components';
import { useBlocker, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { createGeoItem, type UploadedGeoItemPhoto, uploadGeoItemPhoto } from '~/api/projects2';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useGetProject } from '~/hooks/useGetProject';
import { useInvalidateGeoItems } from '../potholing/common';
import {
  buildFieldNotePayload,
  buildProjectAreaChecker,
  buildThumbnailFile,
  type ImportBudget,
  ImportLimitError,
  MAX_IMAGE_COUNT,
  newImportBudget,
  readDroppedFiles,
  readGps,
  toUploadFile,
  wrapIndex,
} from './importPhotos';

type AcceptedPhoto = {
  id: string;
  // name + byte size, the key a repeat drop of the same photo is recognised by.
  dedupeKey: string;
  name: string;
  file: File;
  lat: number;
  lng: number;
  heading: number | null;
  previewUrl: string;
};
type RejectedPhoto = { id: string; name: string; reason: string };
type Stage = 'idle' | 'reading' | 'previewing' | 'creating' | 'done';

// Photos whose GPS is within this distance of the project boundary count as "in area".
const AREA_TOLERANCE_MILES = 1;

// Cap each dropped file so FileUpload rejects it instead of applying its silent 1 GB default;
// aligned with readZipImages' uncompressed-total ceiling.
const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;

// Extensions the drop target lets through. The formats we can't decode are listed too, so a phone's
// HEICs reach us and are reported as skipped instead of failing the dropzone's type filter.
const ACCEPTED_EXTENSIONS = ['.zip', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.tif', '.tiff'];

// Re-render cadence of the read counter, in photos. Per-photo would re-render the grid hundreds of
// times during the EXIF pass.
const READ_PROGRESS_STEP = 10;

const REJECTION_REASONS: Record<string, string> = {
  'file-too-large': 'Too large',
  'file-invalid-type': 'Unsupported format (use JPG, PNG, WEBP or a .zip)',
  'too-many-files': `Too many files selected (over ${MAX_IMAGE_COUNT})`,
};

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
// EXIF heading in degrees to a rough cardinal label for display.
const cardinal = (deg: number) => COMPASS_POINTS[Math.round(deg / 45) % 8];

// Drop photos, a folder of photos, or a zip of them, read EXIF GPS in-browser, and create one
// 'image' field note per geotagged photo. Successive drops add to the same preview set, deduped by
// name and size, so a zip and a handful of loose photos can go in together. Lets the user preview
// and pick which photos to import, warns when a photo falls outside the project's area, resumes
// failed uploads, and guards against in-app navigation mid-import. Rendered inside the Field Notes
// tab; `onImported` fires after a run that created at least one note so the host can refresh its
// list. Gated by the bulk-photo-import flag.
export function TabDetails({
  onImported,
  onBusyChange,
  onViewerOpenChange,
}: {
  onImported?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onViewerOpenChange?: (open: boolean) => void;
}) {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const invalidateGeoItems = useInvalidateGeoItems({ projectId });
  const { data: project } = useGetProject({ projectId });

  // Caches each photo's upload result so a retry reuses the already-uploaded S3 object instead of
  // uploading a second copy (only createGeoItem is retried).
  const uploadedRef = useRef<Map<string, UploadedGeoItemPhoto>>(new Map());
  // Same for the generated thumbnail, so a retry doesn't re-encode and re-upload it.
  const thumbnailPathRef = useRef<Map<string, string>>(new Map());

  // Fires once when the import panel opens.
  useEffect(() => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectPhotoImportOpened, { project_id: projectId });
  }, [projectId]);

  const [stage, setStage] = useState<Stage>('idle');
  const [accepted, setAccepted] = useState<AcceptedPhoto[]>([]);
  const [rejected, setRejected] = useState<RejectedPhoto[]>([]);
  const [body, setBody] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [readProgress, setReadProgress] = useState({ done: 0, total: 0 });
  const abortRef = useRef<AbortController | null>(null);
  // What the drops read so far have spent, so the import caps span the session, not one drop.
  const budgetRef = useRef<ImportBudget>(newImportBudget());
  // Monotonic so ids stay unique as later drops append to the set, keeping the upload caches keyed
  // on them valid.
  const nextId = useRef(0);
  const makeId = (prefix: string, name: string) => `${prefix}-${nextId.current++}-${name}`;

  // Photo ids whose GPS falls outside the project boundary (+ tolerance). Recomputes when the
  // project geometry loads. Empty when there is no boundary (checker fails open).
  const outOfAreaIds = useMemo(() => {
    const inArea = buildProjectAreaChecker(project?.geometry, AREA_TOLERANCE_MILES);
    const ids = new Set<string>();
    for (const photo of accepted) {
      if (!inArea(photo.lng, photo.lat)) ids.add(photo.id);
    }
    return ids;
  }, [accepted, project?.geometry]);

  // Selection is editable only while previewing; during create/done the set is frozen.
  const selectable = stage === 'previewing';
  const photosToCreate = accepted.filter((photo) => selectedIds.has(photo.id));
  const selectedCount = photosToCreate.length;
  const doneCount = photosToCreate.filter((photo) => doneIds.has(photo.id)).length;
  // Selected photos still to create; a drop made after an import leaves created ones in the set.
  const remaining = photosToCreate.length - doneCount;

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectAll = () => setSelectedIds(new Set(accepted.map((p) => p.id)));
  const deselectAll = () => setSelectedIds(new Set());
  const deselectOutOfArea = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of outOfAreaIds) next.delete(id);
      return next;
    });

  // Block in-app navigation while an import runs (component would unmount and lose progress), and
  // arm the native refresh/close prompt only while creating. useBlocker enables beforeunload
  // unconditionally unless enableBeforeUnload is given, so it must gate on stage too. A browser
  // tab/window switch keeps the page mounted, so neither fires — the import keeps running.
  const blocker = useBlocker({
    shouldBlockFn: () => stage === 'creating',
    enableBeforeUnload: () => stage === 'creating',
    withResolver: true,
  });

  // Revoke only the previews an update dropped, since a later drop appends to the set and the
  // earlier photos' URLs are still on screen.
  const previewUrls = useRef<string[]>([]);
  useEffect(() => {
    const current = accepted.map((p) => p.previewUrl);
    const kept = new Set(current);
    previewUrls.current.forEach((url) => {
      if (!kept.has(url)) URL.revokeObjectURL(url);
    });
    previewUrls.current = current;
  }, [accepted]);

  // Revoke whatever is still held when the tab unmounts.
  useEffect(
    () => () => {
      previewUrls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const stepLightbox = (delta: number) =>
    setLightboxIndex((i) => (i === null ? i : wrapIndex(i, delta, accepted.length)));

  // Thumbnail buttons by photo id, so closing the viewer can return focus (and scroll) to the photo.
  const cardButtons = useRef<Map<string, HTMLButtonElement>>(new Map());
  const closeViewer = (photoId: string | undefined) => {
    setLightboxIndex(null);
    if (photoId) requestAnimationFrame(() => cardButtons.current.get(photoId)?.focus());
  };

  // Arrow keys step through photos while the viewer is open, wrapping at both ends; Escape closes it.
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i === null ? i : wrapIndex(i, -1, accepted.length)));
      else if (e.key === 'ArrowRight') setLightboxIndex((i) => (i === null ? i : wrapIndex(i, 1, accepted.length)));
      else if (e.key === 'Escape') closeViewer(accepted[lightboxIndex]?.id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Abort any in-flight import if the view unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = () => {
    abortRef.current?.abort();
    uploadedRef.current.clear();
    thumbnailPathRef.current.clear();
    budgetRef.current = newImportBudget();
    setAccepted([]);
    setRejected([]);
    setBody('');
    setSelectedIds(new Set());
    setLightboxIndex(null);
    setDoneIds(new Set());
    setError(null);
    setReadProgress({ done: 0, total: 0 });
    setStage('idle');
  };

  // Turns dropzone rejections into skipped entries rather than an outright failure, so a selection
  // holding one unreadable file still imports its photos.
  const handleRejections = (rejections: FileRejection[]) => {
    if (rejections.length === 0) return;
    const overCap = (rejection: FileRejection) => rejection.errors.some((e) => e.code === 'too-many-files');
    // The dropzone tags every file that cleared type and size with this one error, so a selection
    // over the cap would otherwise append thousands of identical skipped rows; one line says it
    // better. Rejections of other kinds in the same selection are still listed.
    if (rejections.some(overCap)) setError(`Select at most ${MAX_IMAGE_COUNT} files at a time.`);
    const listed = rejections.filter((rejection) => !overCap(rejection));
    if (listed.length > 0) {
      setRejected((prev) => [
        ...prev,
        ...listed.map((rejection) => ({
          id: makeId('dropped', rejection.file.name),
          name: rejection.file.name,
          reason: REJECTION_REASONS[rejection.errors[0]?.code ?? ''] ?? 'Could not be read',
        })),
      ]);
    }
    setStage((prev) => (prev === 'idle' ? 'previewing' : prev));
  };

  // Reads a dropped selection of photos, folders and/or zips and appends the geotagged photos to the
  // preview set. Photos already loaded, and those the read could not use, are listed as skipped.
  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    setReadProgress({ done: 0, total: 0 });
    setStage('reading');
    try {
      // A copy, committed only once the read returns, so a drop that trips a cap doesn't leave the
      // session budget charged for photos that never reached the preview set.
      const budget = { ...budgetRef.current };
      const { images, oversized, unsupported, ignored, unreadable, zipCount, looseCount } = await readDroppedFiles(
        files,
        budget,
      );
      budgetRef.current = budget;
      setReadProgress({ done: 0, total: images.length });
      const nextAccepted: AcceptedPhoto[] = [];
      const nextRejected: RejectedPhoto[] = [
        ...oversized.map((name) => ({ id: makeId('over', name), name, reason: 'Too large' })),
        ...unsupported.map((name) => ({
          id: makeId('format', name),
          name,
          reason: 'Unsupported format (use JPG or PNG)',
        })),
        ...ignored.map((name) => ({ id: makeId('other', name), name, reason: 'Not an image' })),
        ...unreadable.map((name) => ({ id: makeId('unreadable', name), name, reason: 'Could not be read' })),
      ];
      // Photos already in the preview set, so re-dropping a folder or adding a zip that overlaps it
      // doesn't create the same field note twice.
      const seen = new Set(accepted.map((photo) => photo.dedupeKey));
      let read = 0;
      for (const image of images) {
        read++;
        if (read % READ_PROGRESS_STEP === 0) setReadProgress({ done: read, total: images.length });
        const dedupeKey = `${image.name}:${image.blob.size}`;
        if (seen.has(dedupeKey)) {
          nextRejected.push({ id: makeId('dupe', image.name), name: image.name, reason: 'Already added' });
          continue;
        }
        seen.add(dedupeKey);
        const gps = await readGps(image.blob);
        if (!gps) {
          nextRejected.push({ id: makeId('nogps', image.name), name: image.name, reason: 'No GPS in EXIF' });
          continue;
        }
        nextAccepted.push({
          id: makeId('photo', image.name),
          dedupeKey,
          name: image.name,
          file: toUploadFile(image),
          lat: gps.lat,
          lng: gps.lng,
          heading: gps.heading,
          previewUrl: URL.createObjectURL(image.blob),
        });
      }
      setReadProgress({ done: images.length, total: images.length });
      setAccepted((prev) => [...prev, ...nextAccepted]);
      setRejected((prev) => [...prev, ...nextRejected]);
      setSelectedIds((prev) => new Set([...prev, ...nextAccepted.map((photo) => photo.id)]));
      setStage('previewing');
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectPhotoImportZipRead, {
        project_id: projectId,
        source: zipCount > 0 ? (looseCount > 0 ? 'mixed' : 'zip') : 'files',
        zip_count: zipCount,
        loose_file_count: looseCount,
        total_images: images.length + oversized.length + unsupported.length,
        geotagged_count: nextAccepted.length,
        skipped_count: nextRejected.length,
      });
    } catch (e) {
      console.error('Failed to read dropped photos', e);
      setError(
        e instanceof ImportLimitError
          ? e.message
          : 'Could not read those files. Drop photos (JPG, PNG, WEBP) or a .zip of photos.',
      );
      setStage(accepted.length > 0 ? 'previewing' : 'idle');
    }
  };

  // Creates a field note per selected photo, sequentially. Skips already-created photos, so re-running
  // after a disruption resumes from where it stopped. Aborts cleanly when the signal fires.
  const runImport = async () => {
    const target = photosToCreate.filter((photo) => !doneIds.has(photo.id));
    if (target.length === 0) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setStage('creating');
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectPhotoImportStarted, {
      project_id: projectId,
      selected_count: photosToCreate.length,
      out_of_area_count: photosToCreate.filter((photo) => outOfAreaIds.has(photo.id)).length,
      has_note: body.trim().length > 0,
      is_retry: doneIds.size > 0,
    });
    let attempted = 0;
    let created = 0;
    for (const photo of target) {
      if (controller.signal.aborted) break;
      attempted++;
      try {
        let uploaded = uploadedRef.current.get(photo.id);
        if (!uploaded) {
          uploaded = await uploadGeoItemPhoto(projectId, photo.file, controller.signal);
          uploadedRef.current.set(photo.id, uploaded);
        }
        // A stored thumbnail is what keeps these photos visible in the mobile grid offline, where the
        // per-photo server transcode clients otherwise fall back to is skipped. Best effort: on
        // failure the note is still created and that fallback applies.
        let thumbnailPath = thumbnailPathRef.current.get(photo.id);
        if (!thumbnailPath) {
          const thumbnail = await buildThumbnailFile(photo.file);
          if (thumbnail) {
            try {
              const uploadedThumbnail = await uploadGeoItemPhoto(projectId, thumbnail, controller.signal);
              thumbnailPath = uploadedThumbnail.path;
              thumbnailPathRef.current.set(photo.id, thumbnailPath);
            } catch (e) {
              if (controller.signal.aborted) break;
              console.warn('Could not upload thumbnail for', photo.name, e);
            }
          }
        }
        await createGeoItem(
          projectId,
          buildFieldNotePayload({
            lat: photo.lat,
            lng: photo.lng,
            heading: photo.heading,
            body,
            photo: uploaded,
            thumbnailPath,
          }),
          controller.signal,
        );
        created++;
        setDoneIds((prev) => {
          const next = new Set(prev);
          next.add(photo.id);
          return next;
        });
      } catch (e) {
        if (controller.signal.aborted) break;
        console.error('Failed to create field note for', photo.name, e);
      }
    }
    if (abortRef.current === controller) abortRef.current = null;
    invalidateGeoItems();
    if (created > 0) onImported?.();
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectPhotoImportCompleted, {
      project_id: projectId,
      attempted_count: attempted,
      created_count: created,
      failed_count: attempted - created,
      aborted: controller.signal.aborted,
    });
    if (!controller.signal.aborted) setStage('done');
  };

  const busy = stage === 'reading' || stage === 'creating';
  // Lets a host (the Field Notes dialog) block close while a read/import is in flight.
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  const lightboxPhoto = lightboxIndex === null ? undefined : accepted[lightboxIndex];
  // Lets the host hide its own close X while the viewer shows its own.
  const viewerOpen = lightboxPhoto !== undefined;
  useEffect(() => {
    onViewerOpenChange?.(viewerOpen);
  }, [viewerOpen, onViewerOpenChange]);

  // Opening the viewer hides the thumbnail that had focus, so focus moves to the viewer's close X.
  const viewerCloseRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (viewerOpen) viewerCloseRef.current?.focus();
  }, [viewerOpen]);

  return (
    <div className="flex flex-col gap-4" data-testid="drop-photos-tab">
      <div className={viewerOpen ? 'hidden' : 'flex flex-col gap-4'}>
        <div className="flex flex-col gap-1">
          <Text className="text-base font-semibold text-primary">Import photos as field notes</Text>
          <Text className="text-xs text-grey-70">
            Drop photos, a folder of photos, or a .zip — or add them a few at a time. Each photo's GPS location (from
            EXIF) becomes a field note. Photos without GPS are skipped.
          </Text>
        </div>

        <FileUpload
          title="Photos"
          accept={ACCEPTED_EXTENSIONS}
          maxFiles={MAX_IMAGE_COUNT}
          maxFileSize={MAX_FILE_BYTES}
          keepAcceptedOnRejection
          uploadText="Drag photos or a .zip here, or click to select"
          disabled={busy}
          data-testid="drop-photos-zip-upload"
          onFilesSelected={handleFiles}
          onFileRejection={handleRejections}
        />

        {error && (
          <Text className="text-xs text-red-50" data-testid="drop-photos-error">
            {error}
          </Text>
        )}

        {stage === 'reading' && (
          <Text className="text-xs text-grey-70" data-testid="drop-photos-reading">
            Reading photos…
            {readProgress.total > 0
              ? ` ${Math.min(readProgress.done, readProgress.total)} / ${readProgress.total}`
              : ''}
          </Text>
        )}

        {(stage === 'previewing' || stage === 'creating' || stage === 'done') && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <Text className="text-sm font-medium text-primary" data-testid="drop-photos-summary">
                {accepted.length} geotagged {accepted.length === 1 ? 'photo' : 'photos'} · {selectedCount} selected
                {rejected.length > 0 ? ` · ${rejected.length} skipped` : ''}
              </Text>
              {selectable && accepted.length > 0 && (
                <button
                  type="button"
                  onClick={selectedCount === accepted.length ? deselectAll : selectAll}
                  className="cursor-pointer whitespace-nowrap text-xs text-blue-50 underline"
                  data-testid="drop-photos-select-all"
                >
                  {selectedCount === accepted.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>

            {outOfAreaIds.size > 0 && (
              <div className="flex items-start justify-between gap-2">
                <Text className="text-xs font-medium text-red-50" data-testid="drop-photos-area-warning">
                  {outOfAreaIds.size} of {accepted.length} {outOfAreaIds.size === 1 ? 'photo is' : 'photos are'} outside{' '}
                  {project?.name ? `“${project.name}’s”` : 'this project’s'} area (more than {AREA_TOLERANCE_MILES} mi
                  from its boundary).
                </Text>
                {selectable && (
                  <button
                    type="button"
                    onClick={deselectOutOfArea}
                    className="cursor-pointer whitespace-nowrap text-xs text-blue-50 underline"
                    data-testid="drop-photos-deselect-outside"
                  >
                    Deselect the {outOfAreaIds.size} outside
                  </button>
                )}
              </div>
            )}

            {accepted.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4" data-testid="drop-photos-accepted">
                {accepted.map((photo, index) => {
                  const outOfArea = outOfAreaIds.has(photo.id);
                  const selected = selectedIds.has(photo.id);
                  const border = outOfArea ? 'border-red-50' : selected ? 'border-blue-50' : 'border-grey-30';
                  return (
                    <div
                      key={photo.id}
                      className={`flex flex-col gap-1 rounded-sm border p-1 ${border} ${selected ? '' : 'opacity-60'}`}
                    >
                      <div className="relative">
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) cardButtons.current.set(photo.id, el);
                            else cardButtons.current.delete(photo.id);
                          }}
                          onClick={() => setLightboxIndex(index)}
                          className="group relative block w-full cursor-pointer rounded-[2px] focus-ring"
                          aria-label={`Preview ${photo.name}`}
                          data-testid={`drop-photos-card-open-${photo.id}`}
                        >
                          <img
                            src={photo.previewUrl}
                            alt={photo.name}
                            className="h-24 w-full rounded-[2px] object-cover"
                          />
                          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[2px] bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                            <Icon icon="magnifyingGlass" size={20} className="text-white" />
                          </span>
                        </button>
                        <div className="absolute right-0 top-0 z-10 m-2 rounded bg-white/90 p-0.5 shadow-sm">
                          <Checkbox
                            className="block"
                            checked={selected}
                            onCheckedChange={() => toggleSelected(photo.id)}
                            disabled={!selectable}
                            aria-label={`Include ${photo.name}`}
                            data-testid={`drop-photos-card-checkbox-${photo.id}`}
                          />
                        </div>
                      </div>
                      <Text className="truncate text-[10px] font-medium text-grey-90" title={photo.name}>
                        {photo.name}
                      </Text>
                      <Text className="text-[10px] text-grey-60">
                        {photo.lat.toFixed(5)}, {photo.lng.toFixed(5)}
                      </Text>
                      {doneIds.has(photo.id) && (
                        <Text className="text-[10px] font-medium text-green-60">Field note created</Text>
                      )}
                      {outOfArea && <Text className="text-[10px] font-medium text-red-50">Outside project area</Text>}
                    </div>
                  );
                })}
              </div>
            )}

            {rejected.length > 0 && (
              <details data-testid="drop-photos-rejected">
                <summary className="cursor-pointer text-xs text-grey-70">{rejected.length} skipped</summary>
                <ul className="mt-1 list-disc pl-5">
                  {rejected.map((r) => (
                    <li key={r.id} className="text-[10px] text-grey-60">
                      {r.name} · {r.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {stage === 'previewing' && accepted.length > 0 && (
              <div className="flex flex-col gap-2">
                <label className="flex flex-col gap-1">
                  <Text className="text-xs font-medium text-grey-70">Note (applied to every photo, optional)</Text>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={2}
                    className="w-full rounded-sm border border-grey-30 p-2 text-xs"
                    data-testid="drop-photos-body"
                  />
                </label>
                <div className="flex items-center gap-2">
                  <Button onClick={runImport} disabled={remaining === 0} data-testid="drop-photos-create">
                    Create {remaining} field {remaining === 1 ? 'note' : 'notes'}
                  </Button>
                  <Button variant="secondary" onClick={reset} data-testid="drop-photos-reset">
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {stage === 'creating' && (
              <Text className="text-xs text-grey-70" data-testid="drop-photos-progress">
                Creating field notes… {doneCount} / {photosToCreate.length}. You can switch tabs; this keeps running.
              </Text>
            )}

            {stage === 'done' && (
              <div className="flex flex-col gap-2">
                {remaining === 0 ? (
                  <Text className="text-sm font-medium text-green-60" data-testid="drop-photos-done">
                    Created {doneCount} field {doneCount === 1 ? 'note' : 'notes'}. They appear on the Field Notes tab.
                  </Text>
                ) : (
                  <Text className="text-sm font-medium text-red-50" data-testid="drop-photos-done">
                    Created {doneCount} of {photosToCreate.length}; {remaining} {remaining === 1 ? 'photo' : 'photos'}{' '}
                    could not be uploaded.
                  </Text>
                )}
                <div className="flex items-center gap-2">
                  {remaining > 0 && (
                    <Button onClick={runImport} data-testid="drop-photos-retry">
                      Retry {remaining} failed
                    </Button>
                  )}
                  <Button variant="secondary" onClick={reset} data-testid="drop-photos-import-more">
                    Start over
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {lightboxPhoto && (
        <div className="flex flex-col gap-3" data-testid="drop-photos-lightbox">
          {/* Same spot as the host dialog's X, which is hidden while the viewer is open. */}
          <button
            ref={viewerCloseRef}
            type="button"
            onClick={() => closeViewer(lightboxPhoto.id)}
            className="absolute right-4 top-4 flex h-5 w-5 cursor-pointer items-center justify-center rounded-[6px] opacity-70 transition-opacity hover:opacity-100 focus-ring"
            aria-label="Close photo"
            data-testid="drop-photos-lightbox-close"
          >
            <Icon icon="cross2" size={20} className="text-primary-dark" />
          </button>
          <div className="flex items-center gap-2">
            {accepted.length > 1 && (
              <>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => stepLightbox(-1)}
                  data-testid="drop-photos-lightbox-prev"
                >
                  <Icon icon="chevronLeft" size={14} /> Prev
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => stepLightbox(1)}
                  data-testid="drop-photos-lightbox-next"
                >
                  Next <Icon icon="chevronRight" size={14} />
                </Button>
              </>
            )}
            <Text className="text-xs text-grey-60">
              {(lightboxIndex ?? 0) + 1} / {accepted.length}
            </Text>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex w-48 shrink-0 flex-col gap-1">
              <Text className="truncate text-sm font-medium text-grey-90" title={lightboxPhoto.name}>
                {lightboxPhoto.name}
              </Text>
              <Text className="text-xs text-grey-70">
                {lightboxPhoto.lat.toFixed(5)}, {lightboxPhoto.lng.toFixed(5)}
              </Text>
              {typeof lightboxPhoto.heading === 'number' && (
                <Text className="text-xs text-grey-70" data-testid="drop-photos-lightbox-heading">
                  Heading {Math.round(lightboxPhoto.heading)}° ({cardinal(lightboxPhoto.heading)})
                </Text>
              )}
              {outOfAreaIds.has(lightboxPhoto.id) && (
                <Text className="text-xs font-medium text-red-50">Outside project area</Text>
              )}
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  checked={selectedIds.has(lightboxPhoto.id)}
                  onCheckedChange={() => toggleSelected(lightboxPhoto.id)}
                  disabled={!selectable}
                  aria-label={`Include ${lightboxPhoto.name}`}
                  data-testid="drop-photos-lightbox-include"
                />
                <Text className="text-xs text-grey-70">Include this photo</Text>
              </div>
            </div>
            <div className="flex min-w-0 flex-1 justify-center bg-grey-10">
              <img
                src={lightboxPhoto.previewUrl}
                alt={lightboxPhoto.name}
                className="max-h-[55vh] w-auto object-contain"
              />
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <DialogContent data-testid="drop-photos-leave-guard" title="Stop importing photos?">
          <DialogBody>
            <DialogDescription>
              An import is still running. Leaving this page will stop it, and the remaining {remaining}{' '}
              {remaining === 1 ? 'photo' : 'photos'} won’t be created. Switching browser tabs is fine — the import keeps
              running.
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => blocker.reset?.()} data-testid="drop-photos-stay">
              Keep importing
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                abortRef.current?.abort();
                blocker.proceed?.();
              }}
              data-testid="drop-photos-leave"
            >
              Leave and stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
